/**
 * widePolylineFillMesh — the SOLID interior of a wide polyline's band.
 *
 * # What AutoCAD draws, and what the canvas drew before this
 *
 * A 2D POLYLINE / LWPOLYLINE carrying DXF width (groups 40 / 41 per vertex, 43
 * entity-level on an LWPOLYLINE, 40 / 41 entity-level on a heavy POLYLINE) is
 * drawn as a mitred, square-capped BAND, and the `FILLMODE` system variable
 * says whether that band is painted or merely outlined. Autodesk's
 * system-variable reference: FILLMODE "specifies whether hatches and fills, 2D
 * solids, and wide polylines are filled in" — type Integer, INITIAL VALUE 1,
 * saved in the drawing. `ezdxf` agrees on both halves: `$FILLMODE` is group
 * code 70 with `default=1` (`sections/headervars.py`), and its renderer, on
 * `entity.has_width`, builds a `TraceBuilder`, calls `draw_filled_polygon` with
 * a SOLID `Filling()` for every band polygon and RETURNS
 * (`addons/drawing/frontend.py`) — the centreline is never stroked and the
 * silhouette is never outlined.
 *
 * The vector plot already did exactly that
 * (`print/pdf/converters/widePolylineBand.ts`, a fill-only `PathPrimitive`
 * with `stroke: false`). The CANVAS did not: `batchedLayer.ts` is a `gl.LINES`
 * batcher, so it emitted the silhouette loops and nothing covered the interior
 * — a hollow ribbon that let whatever lies underneath show through the middle
 * of every wide polyline, which is AutoCAD's FILLMODE 0 appearance shown for a
 * FILLMODE 1 drawing (@codex PR #785 round 11, F2; the residual was recorded
 * in `cad-core`'s `polylineWidthOutline.ts` header and in the plot converter's
 * header, and this module closes it). Screen and paper now agree on the
 * interior as well as on the silhouette, the extents, the taper, the joints,
 * the caps and the arc a bulged span follows — because both read the SAME loops
 * from the same module.
 *
 * # The fill is ADDITIVE: the outline is still stroked
 *
 * This mesh covers the interior; it does NOT replace the silhouette. The
 * canvas keeps stroking the band's boundary in both fill states, for two
 * reasons the plot does not have to care about and that
 * `batchedLayer.ts::widePolylineLineVertices` states in full: the per-instance
 * SELECTION byte is addressed by a LINE-bucket slot, so a mesh-only band could
 * never highlight; and triangle rasterisation has no minimum-width floor, so a
 * sub-pixel band (group 43 = 0.15, the width most of this repo's fixtures
 * carry) could thin out or vanish when zoomed out, where a lineweight stroke
 * is always at least a pixel. The mesh therefore renders UNDER the line
 * buckets — see {@link widePolylineFillRenderOrder}.
 *
 * # No second band, and no second triangulator
 *
 * The loops are `adapters/geom/polylineDrawnBand.ts::drawnPolylineBandLoops` —
 * the shared gate around `widePolylineBandLoops`, the one the batcher, window
 * select, the pick and the plot all call. The triangulation is
 * `hatch/earcutLoops.ts::tessellateHatchLoops`, the same exterior-minus-holes
 * earcut pass the HATCH fill runs, and the buffer packing is
 * `./fillMeshGeometry.ts`, shared with the hatch and wipeout factories. This
 * module contributes only the two decisions that are its own: which ring is the
 * exterior, and which PLANE to earcut in.
 *
 * # Why the plane matters
 *
 * earcut is 2D. A heavy POLYLINE with a non-default group-210 extrusion has its
 * band built in its own OCS plane, and that plane's WCS-XY shadow is squashed —
 * for extrusion (0,1,0) it has exactly ZERO area, so earcutting the world
 * coordinates would produce no triangles at all and the band would vanish. So
 * the rings are taken in the band's OWN frame (`bandLoopsInPlane`) and the
 * finished triangle vertices are lifted back through
 * `plane.toWorld`. With the default extrusion there is no plane and no mapping
 * — the common case is untouched.
 *
 * # Scope of the FILLMODE read
 *
 * FILLMODE governs three object classes in AutoCAD: hatches and fills, 2D
 * SOLIDs, and wide polylines. Where each stands:
 *
 *   - WIDE POLYLINE — read here, and on paper in
 *     `print/pdf/converters/widePolylineBand.ts` (@codex round 11 / round 12).
 *   - HATCH — read since @codex round 16 (P2), on BOTH surfaces and for all
 *     three fill tiers: `sceneMeshFactories.ts::buildHatchFillMesh` emits no
 *     solid, gradient or pattern fill at 0 and `batchedLayer.ts::
 *     hatchOutlineLineVertices` strokes the boundary loops instead, while
 *     `print/pdf/converters/hatchConverter.ts` emits the stroke-only twin of
 *     the same loops. The joint assertion is
 *     `print/pdf/converters/hatchFillmodeCanvasParity.test.ts`.
 *   - 2D SOLID / TRACE — NOT APPLICABLE: neither is a typed entity. Both are
 *     carried as `rawEntityGroupSchema` unsupported-entity groups (`model.ts`)
 *     for round-trip fidelity and are neither rendered nor plotted, so there is
 *     no fill anywhere to gate.
 *
 * Two residuals remain, declared rather than silently simplified.
 *
 * FIRST — the hatch PICK. `raycast/distance.ts` and `raycast/windowSelect.ts`
 * still treat a hatch's interior as pickable in BOTH fill states, where the
 * wide-polyline arms of those same files have gated on `fillModePaintsSolid`
 * since round 11/12. AutoCAD picks an unfilled hatch by its boundary, not by
 * its interior, so at `$FILLMODE 0` a click in the middle of a hatch selects
 * something the user cannot see. The render half is closed here; the pick half
 * is a read in those two arms, exactly like the band's.
 *
 * SECOND — a SOLID ARROWHEAD (`isFilledArrow` — closed-filled / closed / dot,
 * on DIMENSION and MULTILEADER). It is a 2D SOLID inside AutoCAD's anonymous
 * dimension block, and AutoCAD draws it OPEN at FILLMODE 0 — but neither the
 * canvas arrow meshes
 * (`scene/sceneDimensionMeshFactories.ts`) nor the plot arrow paths
 * (`print/pdf/converters/dimensionConverter.ts`, `multileaderConverter.ts`)
 * read the variable yet. It is left whole deliberately: closing it on ONE
 * surface would recreate exactly the screen-versus-paper split rounds 12 and 16
 * closed, so both halves land together or not at all.
 *
 * NOT governed by FILLMODE, and deliberately untouched: a WIPEOUT's mask (an
 * image-like frame controlled by WIPEOUTFRAME / IMAGEFRAME, not a filled
 * object) and an OLE2FRAME (which emits no fill at all).
 */

import * as THREE from "three";

import {
  bandLoopsInPlane,
  bandRingRoles,
  drawnPolylineBandLoops,
  fillModePaintsSolid,
} from "../adapters/geom/polylineDrawnBand";
import {
  fillStratumRenderOrder,
  type DrawOrderStrata,
} from "../drawOrderStrata";
import { tessellateHatchLoops } from "../hatch/earcutLoops";
import { directMeshLayerStamp } from "../layerChainVisibility";
import type { RenderableEntity } from "../types";

import { tessellationToBufferGeometry } from "./fillMeshGeometry";

/**
 * Draw order for the band fill: the DRAWORDER position of the entity it
 * belongs to, strictly below that entity's OWN outline.
 *
 * The stack it slots into is HATCH (-2) < WIPEOUT mask (-1) < **the band fills
 * and the line buckets, interleaved in DRAWORDER within [-0.5, 1)** < arrow
 * decorators (1) < text (2). Nothing is depth-written at this layer, so within
 * one frame `renderOrder` alone decides who paints last.
 *
 * A single GLOBAL constant cannot express this and the previous `-0.5` did
 * not: it put every band fill below every line bucket, so a line authored
 * EARLIER than the band still painted over the band's solid interior (Codex PR
 * #785 round 12, P1). Flipping the constant to the other side of the buckets
 * is not the fix either — the band's own silhouette is stroked into a line
 * bucket (`batchedLayer.ts::widePolylineLineVertices`), and that stroke is
 * what carries the per-instance SELECTED / HOVERED colour and the minimum
 * on-screen width, so an interior painting over it would leave a cyan edge
 * erased by a body in the entity's own colour.
 *
 * `drawOrderStrata.ts` reconciles the two by cutting the line buckets into
 * DRAWORDER strata wherever a band could cover something authored before it,
 * and this fill takes `fillStratumRenderOrder` of its own stratum: strictly
 * above the previous stratum's line work, strictly below its own. Strictly —
 * never equal, so three.js's per-frame sort is never asked to break a tie
 * between a band's interior and its own outline. A drawing that needs no cut
 * plans one stratum and reproduces the old numbers exactly (lines 0, fill
 * -0.5), which is also the floor: no number of strata can push a fill under
 * the wipeout mask or line work over an arrow head.
 *
 * `depthWrite` stays FALSE on the fill material, and ordering being correct is
 * the reason it must. The band, its own outline and any coincident line work
 * sit in the SAME plane; the depth buffer therefore cannot separate them, and
 * `LessEqualDepth` at equal depth resolves to "whoever draws last", i.e. to
 * `renderOrder`. Writing depth would not add ordering — it would only let the
 * fill REJECT later fragments at its own elevation, starting with the outline
 * it is supposed to render under, and would z-fight with every coincident
 * entity. Painter's order is the mechanism here; the depth buffer must stay
 * out of it. (This is the same choice `buildHatchFillMesh` makes, for the same
 * reason.)
 *
 * @param strata the plan the owning `BatchedLayer.bulkLoad` cut
 *   (`BatchedLayerApi.getDrawOrderStrata`) — the SAME plan the bucket meshes
 *   read, so the two sides of the painter sequence cannot drift.
 */
export function widePolylineFillRenderOrder(
  strata: DrawOrderStrata,
  renderable: RenderableEntity,
): number {
  return fillStratumRenderOrder(strata.stratumOf(renderable));
}

/**
 * The filled band of a width-carrying polyline, or `null` when this renderable
 * does not draw one: a non-polyline, a polyline with no authored width, a
 * degenerate band that bounds no area, or a drawing whose `$FILLMODE` is 0 (in
 * which case the band is an OUTLINE, and the silhouette `batchedLayer.ts`
 * strokes in every state is the whole of it).
 */
export function buildWidePolylineFillMesh(
  renderable: RenderableEntity,
): THREE.Mesh | null {
  const geometry = renderable.geometry;
  if (geometry.kind !== "polyline") return null;
  if (!fillModePaintsSolid(renderable.fillmode)) return null;
  const loops = drawnPolylineBandLoops(geometry);
  if (!loops) return null;

  // earcut in the band's OWN plane (see the module header), then lift the
  // finished triangles back out through the same frame.
  const { local, plane } = bandLoopsInPlane(loops, geometry);
  const roles = bandRingRoles(local);
  const exterior = local[roles.exterior];
  // Fewer than three corners bounds no area — the same guard the plot's
  // `ringToSubpath` applies before it fills.
  if (!exterior || exterior.length < 3) return null;

  const tess = tessellateHatchLoops([
    { vertices: exterior.map((p) => ({ x: p.x, y: p.y, z: p.z })), isHole: false },
    ...roles.holes.map((i) => ({
      vertices: local[i]!.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      isHole: true,
    })),
  ]);
  if (tess.indices.length === 0) return null;

  const bufferGeometry = tessellationToBufferGeometry(
    tess,
    plane ? (p) => plane.toWorld(p) : undefined,
  );

  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(renderable.colorHex).getHex(),
    transparent: false,
    // Matching the hatch fill: the band must not write depth, or coincident
    // line work at the same elevation would z-fight against it.
    depthWrite: false,
    // The band is planar and its winding follows the polyline's, which the DXF
    // does not constrain — so it must be visible from both sides.
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(bufferGeometry, material);
  // Parent-attribute a synthetic block-content child (`renderParentId`
  // present) for the per-viewport visibility mask; a standalone polyline
  // stamps its own id. Same rule the hatch / wipeout factories apply.
  mesh.userData = {
    entityId: renderable.renderParentId ?? renderable.id,
    ...directMeshLayerStamp(renderable),
    kind: "polyline-fill",
  };
  return mesh;
}
